/*
  OK FBI — Phase II: Live Investigator Card + OBS-safe Card Wall.

  Two presentation modes live in this file:

  ● LiveInvestigatorCard — a compact, OBS-safe card. Shows only the
    essential fields: display name, ID number, rank, primary badge (or VIP
    chip), and an optional streak indicator. Every tier keeps a compatible
    grid footprint (same aspect ratio, same padding scaffold) so a ROYAL
    card never breaks the layout — only its prestige (border, glow, chip
    color, gradient) changes.

  ● InvestigatorCardWall — an OBS-safe wall that AUTOMATICALLY resizes and
    rearranges based on the number of joined investigators:
        1–4  → larger cards           (2 cols max, XL scale)
        5–8  → medium cards           (3–4 cols, L scale)
        9–12 → compact cards          (4 cols, M scale)
        13–20 → extra-compact cards   (5 cols, S scale)
        >20   → paging (18-card pages, auto-rotates every ~7s)

  There is NO manual scrolling and NO horizontal overflow. The wall
  reserves a Phase III safe-zone slot (badges / streak icon) on every card
  so the upcoming reward pipeline can populate it without rebuilding.

  The LARGE editing preview in `InvestigatorCard.jsx` is a separate
  presentation mode used only inside the OWNER Card Templates page.
*/
import React, { useEffect, useMemo, useState } from "react";
import { BadgeRail } from "./InvestigatorCard";

// ── Element helpers shared with the large card ────────────────────
function _liveEl(tpl, key) {
  const els = tpl?.layout?.elements || {};
  const legacy = tpl?.layout?.safe_zones?.[key];
  const merged = { ...(legacy || {}), ...(els[key] || {}) };
  if (Object.keys(merged).length === 0) return null;
  if (merged.visible === false) return null;
  return merged;
}

// Slice-1 refinement: element position comes from the template. Density
// only affects TEXT SIZES and the badge rail visible count. This lets the
// SAME layout the OWNER configured in the editor drive the compact wall
// card too — no separate disconnected system.
const DENSITY_TEXT = {
  xl: { name: 20, meta: 12, id: 13, rank: 11, points: 14, streak: 9, badges: 4 },
  lg: { name: 15, meta: 10, id: 11, rank: 9,  points: 12, streak: 9, badges: 3 },
  md: { name: 12, meta: 9,  id: 10, rank: 8,  points: 10, streak: 8, badges: 3 },
  sm: { name: 10, meta: 8,  id: 8,  rank: 7,  points: 8,  streak: 7, badges: 2 },
};

// The live wall scales cards for density, but a font-size configured in the
// existing tier layout must remain authoritative. Scale that configured value
// by the same density ratio used by the default live typography so the editor
// and final card stay visually aligned without allowing overflow at high density.
const DEFAULT_LAYOUT_FONT_PX = {
  name: 28, id: 14, rank: 12, points: 14, streak: 10, tiktok: 11, tier: 9,
};
function liveFontSize(el, key, densityFallback) {
  const configured = Number(el?.font_size_px);
  if (!Number.isFinite(configured) || configured <= 0) return densityFallback;
  const base = DEFAULT_LAYOUT_FONT_PX[key] || 12;
  return Math.max(6, Math.round(configured * (densityFallback / base)));
}

function LiveZone({ el, className = "", children, extraStyle }) {
  if (!el) return null;
  const style = {
    left: `${el.x}%`, top: `${el.y}%`,
    width: `${el.w}%`, height: `${el.h}%`,
    display: "flex", alignItems: "center",
    justifyContent: el.align === "end" ? "flex-end"
                    : el.align === "center" ? "center" : "flex-start",
    zIndex: el.z ?? 1,
    ...(extraStyle || {}),
  };
  return <div className={`absolute ${className}`} style={style}>{children}</div>;
}


const TIER_ACCENTS = {
  standard: {
    border: "border-neutral-700",
    glow:   "shadow-[0_0_18px_rgba(160,160,160,0.10)]",
    gradient: "from-neutral-900 via-neutral-950 to-neutral-900",
    chip: "bg-neutral-800/70 text-neutral-200 border-neutral-700",
    name: "text-neutral-100",
    id:   "text-neutral-300",
    label: "STD",
  },
  streamer: {
    border: "border-emerald-800",
    glow:   "shadow-[0_0_22px_rgba(16,185,129,0.18)]",
    gradient: "from-emerald-950 via-neutral-950 to-neutral-950",
    chip: "bg-emerald-900/70 text-emerald-200 border-emerald-800",
    name: "text-emerald-100",
    id:   "text-emerald-200",
    label: "STR",
  },
  vip: {
    border: "border-yellow-700",
    glow:   "shadow-[0_0_26px_rgba(234,179,8,0.24)]",
    gradient: "from-yellow-950 via-neutral-950 to-neutral-900",
    chip: "bg-yellow-900/60 text-yellow-200 border-yellow-800",
    name: "text-yellow-100",
    id:   "text-yellow-200",
    label: "VIP",
  },
  special_3: {
    border: "border-fuchsia-800",
    glow:   "shadow-[0_0_28px_rgba(217,70,239,0.28)]",
    gradient: "from-fuchsia-950 via-neutral-950 to-neutral-900",
    chip: "bg-fuchsia-900/70 text-fuchsia-200 border-fuchsia-800",
    name: "text-fuchsia-100",
    id:   "text-fuchsia-200",
    label: "SP·3",
  },
  elite_2: {
    border: "border-cyan-700",
    glow:   "shadow-[0_0_32px_rgba(6,182,212,0.32)]",
    gradient: "from-cyan-950 via-neutral-950 to-neutral-900",
    chip: "bg-cyan-900/70 text-cyan-200 border-cyan-800",
    name: "text-cyan-100",
    id:   "text-cyan-200",
    label: "EL·2",
  },
  royal_1: {
    border: "border-amber-500",
    // Royal card gets the most prestige but SAME footprint as any other.
    glow:   "shadow-[0_0_46px_rgba(245,158,11,0.55)] ring-1 ring-amber-400/40",
    gradient: "from-amber-950 via-yellow-900/40 to-neutral-950",
    chip: "bg-amber-900/80 text-amber-100 border-amber-500",
    name: "text-amber-100",
    id:   "text-amber-200",
    label: "R·1",
  },
};


// ── Density tiers — same grid footprint contract across all cards ──
// aspect_ratio kept constant so ROYAL cards NEVER break the layout even
// though they are visually more prestigious.
const DENSITY_TIERS = [
  // { max, cols, minCardPx, fontScale }
  { max: 4,   cols: "repeat(auto-fit, minmax(260px, 1fr))",  size: "xl",  padPx: 12 },
  { max: 8,   cols: "repeat(auto-fit, minmax(210px, 1fr))",  size: "lg",  padPx: 10 },
  { max: 12,  cols: "repeat(auto-fit, minmax(170px, 1fr))",  size: "md",  padPx: 8  },
  { max: 20,  cols: "repeat(auto-fit, minmax(140px, 1fr))",  size: "sm",  padPx: 6  },
];

const PAGE_SIZE = 18;
const PAGE_ROTATE_MS = 7000;

function densityFor(count) {
  for (const d of DENSITY_TIERS) if (count <= d.max) return d;
  // Fallback (shouldn't be reached — paging kicks in above 20).
  return DENSITY_TIERS[DENSITY_TIERS.length - 1];
}


// ── Live card — compact, OBS-safe, essential fields only ──
// Density-aware max badges: xl→6 · lg→4 · md→3 · sm→2. Overflow renders
// a compact "+N" chip via the shared BadgeRail component so a growing
// badge collection NEVER wraps, increases card height, or escapes the
// card boundary.
const BADGE_MAX_BY_SIZE = { xl: 6, lg: 4, md: 3, sm: 2 };

export function LiveInvestigatorCard({ template, participant, size = "md" }) {
  const tpl = template || {};
  const p = participant || {};
  const cat = tpl.category || p.card_category || "standard";
  const accent = TIER_ACCENTS[cat] || TIER_ACCENTS.standard;
  const isCustom = tpl.visual_mode === "custom" && !!tpl.artwork_url;
  const txt = DENSITY_TEXT[size] || DENSITY_TEXT.md;

  // Read all element configs from the same template layout the OWNER
  // configured in the editor. The compact card uses the SAME source of
  // truth as the large card — no disconnected layout system.
  const elAvatar = _liveEl(tpl, "avatar");
  const elName   = _liveEl(tpl, "display_name");
  const elId     = _liveEl(tpl, "agent_id");
  const elRank   = _liveEl(tpl, "rank");
  const elPoints = _liveEl(tpl, "points");
  const elTikTok = _liveEl(tpl, "tiktok_username");
  const elBadges = _liveEl(tpl, "badge_rail") || _liveEl(tpl, "badges");
  const elStreak = _liveEl(tpl, "streak");
  const elTier   = _liveEl(tpl, "tier_label");

  const showStreak = Boolean(p.streak?.months && p.streak.months >= 2);
  const frameClass = isCustom
    ? "rounded-xl border border-transparent"
    : `rounded-xl border ${accent.border} ${accent.glow} bg-gradient-to-br ${accent.gradient}`;

  return (
    <div
      data-testid={`live-card-${cat}`}
      data-visual-mode={isCustom ? "custom" : "default"}
      className={`relative overflow-hidden ${frameClass}`}
      style={{ aspectRatio: "16 / 10" }}
    >
      {/* Artwork background — CSS only, never a broken-image icon */}
      {tpl.artwork_url && (
        <div
          data-testid={`live-card-artwork-${cat}`}
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("${tpl.artwork_url}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: isCustom ? 1 : 0.9,
          }}
        />
      )}
      {/* Legibility scrim — default-mode only. Custom artwork stays authoritative. */}
      {!isCustom && (
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.75) 100%)" }}/>
      )}

      {/* Tier chip — only in default mode; custom artwork provides its own label */}
      {!isCustom && elTier && (
        <LiveZone el={elTier}>
          <div className={`text-[8px] font-mono tracking-[0.24em] px-1.5 py-0.5 rounded border ${accent.chip}`}
               style={{ fontSize: `${liveFontSize(elTier, "tier", 8)}px`,
                        fontFamily: elTier.font_family && elTier.font_family !== "inherit" ? elTier.font_family : undefined,
                        opacity: elTier.opacity ?? 1 }}>
            {accent.label}
          </div>
        </LiveZone>
      )}
      {showStreak && elStreak && (
        <LiveZone el={elStreak}>
          <div className={`font-cairo px-1 py-0.5 rounded border ${accent.chip} truncate`}
               style={{ fontSize: `${liveFontSize(elStreak, "streak", txt.streak)}px`,
                        fontFamily: elStreak.font_family && elStreak.font_family !== "inherit" ? elStreak.font_family : undefined }}>
            {p.streak.months}× شهر
          </div>
        </LiveZone>
      )}

      {/* Avatar — REQUIRED identity element, template-positioned */}
      {elAvatar && (
        <LiveZone el={elAvatar}>
          {p.avatar_url ? (
            <div className="h-full aspect-square border border-yellow-500/40 shadow-md overflow-hidden shrink-0"
                 data-testid={`live-avatar-${cat}`}
                 style={{
                   borderRadius: (elAvatar.shape === "square") ? 0
                                  : (elAvatar.shape === "rounded") ? "12%" : "9999px",
                   backgroundImage: `url("${p.avatar_url}")`,
                   backgroundSize: "cover", backgroundPosition: "center",
                 }}/>
          ) : (
            <div className="h-full aspect-square border border-yellow-500/30 bg-neutral-900/70 flex items-center justify-center text-yellow-500/70 font-cairo shrink-0"
                 data-testid={`live-avatar-fallback-${cat}`}
                 style={{
                   borderRadius: (elAvatar.shape === "square") ? 0
                                  : (elAvatar.shape === "rounded") ? "12%" : "9999px",
                   fontSize: `${Math.max(8, Math.round(txt.meta - 1))}px`,
                 }}>
              {(p.display_name || "OK").slice(0, 1)}
            </div>
          )}
        </LiveZone>
      )}

      {/* Display name */}
      {elName && (
        <LiveZone el={elName}>
          <div className={`font-cairo font-black ${accent.name} leading-tight truncate`}
               style={{
                 fontSize: `${liveFontSize(elName, "name", txt.name)}px`,
                 color: elName.color || undefined,
                 fontFamily: elName.font_family && elName.font_family !== "inherit" ? elName.font_family : undefined,
                 opacity: elName.opacity ?? 1,
                 textShadow: "0 2px 8px rgba(0,0,0,0.75)",
               }}>
            {p.display_name || "—"}
          </div>
        </LiveZone>
      )}

      {/* Badge Rail — stays INSIDE its configured safe zone; density-aware max */}
      {elBadges && (
        <BadgeRail
          badges={p.badges}
          zone={{ x: elBadges.x, y: elBadges.y, w: elBadges.w, h: elBadges.h, align: elBadges.align }}
          accentChip={accent.chip}
          maxVisible={Math.min(elBadges.max_visible ?? txt.badges, txt.badges)}
          size={size}/>
      )}

      {/* Agent ID */}
      {elId && (
        <LiveZone el={elId}>
          <div className={`font-mono ${accent.id} tracking-widest truncate`}
               style={{
                 fontSize: `${liveFontSize(elId, "id", txt.id)}px`,
                 color: elId.color || undefined,
                 opacity: elId.opacity ?? 1,
                 textShadow: "0 2px 6px rgba(0,0,0,0.6)",
               }}>
            #{p.agent_id || "----"}
          </div>
        </LiveZone>
      )}

      {/* Rank */}
      {elRank && (
        <LiveZone el={elRank}>
          <div className="font-cairo text-neutral-300 uppercase tracking-[0.2em] truncate"
               style={{ fontSize: `${liveFontSize(elRank, "rank", txt.rank)}px`, color: elRank.color || undefined,
                        opacity: elRank.opacity ?? 1 }}>
            {p.rank || "rookie"}
          </div>
        </LiveZone>
      )}

      {/* Points */}
      {elPoints && (
        <LiveZone el={elPoints}>
          <div className="font-cairo tabular-nums truncate"
               style={{ fontSize: `${liveFontSize(elPoints, "points", txt.points)}px`,
                        color: elPoints.color || "#fde68a",
                        opacity: elPoints.opacity ?? 1 }}>
            {Number(p.points || 0).toLocaleString("en-US")}
          </div>
        </LiveZone>
      )}

      {/* TikTok handle */}
      {elTikTok && p.tiktok_username && (
        <LiveZone el={elTikTok}>
          <div className="font-mono text-neutral-300 truncate"
               style={{ fontSize: `${liveFontSize(elTikTok, "tiktok", txt.meta)}px`,
                        fontFamily: elTikTok.font_family && elTikTok.font_family !== "inherit" ? elTikTok.font_family : undefined,
                        opacity: elTikTok.opacity ?? 1 }}>
            @{p.tiktok_username}
          </div>
        </LiveZone>
      )}
    </div>
  );
}


// ── The wall ─────────────────────────────────────────────────────
export function InvestigatorCardWall({ participants = [], templatesByCategory = {}, safeAreaClass = "" }) {
  const total = participants.length;
  const paging = total > 20;
  const [pageIdx, setPageIdx] = useState(0);

  const pages = useMemo(() => {
    if (!paging) return [participants];
    const out = [];
    for (let i = 0; i < participants.length; i += PAGE_SIZE) {
      out.push(participants.slice(i, i + PAGE_SIZE));
    }
    return out;
  }, [participants, paging]);

  useEffect(() => {
    if (!paging || pages.length <= 1) return;
    const id = setInterval(
      () => setPageIdx((i) => (i + 1) % pages.length),
      PAGE_ROTATE_MS,
    );
    return () => clearInterval(id);
  }, [paging, pages.length]);

  useEffect(() => {
    if (pageIdx >= pages.length) setPageIdx(0);
  }, [pageIdx, pages.length]);

  const currentPage = pages[pageIdx] || [];
  const density = densityFor(currentPage.length);
  const size = density.size;

  return (
    <div
      data-testid="investigator-card-wall"
      data-participant-count={total}
      data-page-count={pages.length}
      data-density={size}
      className={`relative w-full h-full ${safeAreaClass}`}
    >
      <style>{`
        @keyframes okfbi-wall-in {
          0%   { opacity: 0; transform: translateY(8px) scale(0.98); filter: blur(3px); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        .okfbi-wall-anim > * {
          animation: okfbi-wall-in 500ms ease-out both;
        }
      `}</style>

      <div
        className="okfbi-wall-anim grid w-full h-full"
        style={{
          gridTemplateColumns: density.cols,
          gap: `${density.padPx}px`,
          alignContent: "start",
        }}
      >
        {currentPage.map((p, i) => {
          const cat = p.card_category || "standard";
          const tpl = templatesByCategory[cat] || {};
          return (
            <div key={p.id || `${cat}-${i}`}
                 style={{ animationDelay: `${Math.min(i, 12) * 50}ms` }}>
              <LiveInvestigatorCard template={tpl} participant={p} size={size}/>
            </div>
          );
        })}
      </div>

      {/* Paging indicator — appears only when rotating; positioned inside
          the safe area so it never runs off-screen. */}
      {paging && pages.length > 1 && (
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1.5"
             data-testid="wall-paging-indicator">
          {pages.map((_, i) => (
            <span key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === pageIdx ? "w-6 bg-yellow-400" : "w-1.5 bg-neutral-700"
                  }`}/>
          ))}
        </div>
      )}
    </div>
  );
}
